
import { useAuth, PermissionPayload } from "../../modules/auth";
import CrudArtEngenheiroTransportador from "../../components/transportador/art/CrudArtEngenheiroTransportador.vue";
import {
  useDashboardService,
  DashboarPayload,
  OrgaoRoboStatusPayload
} from "../../modules/business/cliente/dashboardBusiness";
import { defineComponent, reactive, onMounted } from "vue";
import { useToast } from "../../modules/message";
import { PaginationResult, defaultResults } from "../../pagination";
import {
  useOrgaoAetService,
  useOrgaoArtService
} from "../../modules/business/common/commonBusiness";

interface DashboardState {
  entity: DashboarPayload | null;
  page: number;
  responseConsulta: boolean;
  responseCadastro: boolean;
  saldoAntiCaptcha: string | null;
  saldoHcaptcha: string | null;
  results: PaginationResult<DashboarPayload>;
  loadingFila: boolean;
  orgaosRobo: OrgaoRoboStatusPayload[];
  loadingOrgaos: boolean;
}

export default defineComponent({
  components: {
    CrudArtEngenheiroTransportador
  },
  setup() {
    const state = reactive<DashboardState>({
      entity: null,
      page: 0,
      responseConsulta: false,
      responseCadastro: false,
      saldoAntiCaptcha: null,
      saldoHcaptcha: null,
      results: defaultResults(),
      loadingFila: false,
      orgaosRobo: [],
      loadingOrgaos: false
    });

    const dashboardService = useDashboardService();

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const executeBuscaSaldoAntiCaptcha = async () => {
      try {
        await dashboardService.getSaldoAntiCaptcha();
        const saldoResponse = dashboardService.data.value;
        const saldo = saldoResponse.balance;
        state.saldoAntiCaptcha = saldo;
        return saldo;
      } catch (error) {
        useToast().error("Falha ao buscar saldo AntiCaptcha");
        return null;
      }
    };

    const executeBuscaSaldoHcaptcha = async () => {
      try {
        await dashboardService.getSaldoHcaptcha();
        const saldoResponse = dashboardService.data.value;
        const saldo = saldoResponse.saldo;
        state.saldoHcaptcha = saldo;
        return saldo;
      } catch (error) {
        useToast().error("Falha ao buscar saldo Hcaptcha");
        return null;
      }
    };

    const executeBuscaStatusConsulta = async () => {
      await dashboardService.isRoboConsultaHabilitado();
      const consultaResponse = dashboardService.data.value;
      state.responseConsulta = consultaResponse;
      return consultaResponse;
    };

    const executeBuscaStatusCadastro = async () => {
      await dashboardService.isRoboCadastroHabilitado();
      const cadastroResponse = dashboardService.data.value;
      state.responseCadastro = cadastroResponse;
      return cadastroResponse;
    };

    const executeDesligarRoboConsulta = async () => {
      try {
        await useDashboardService().desligarRoboConsulta();
        useToast().success("Robos desligados com sucesso");
        executeBuscaStatusConsulta();
      } catch {
        useToast().error("Falha ao desligar os Robos");
      }
    };

    const executeLigarRoboConsulta = async () => {
      try {
        await useDashboardService().ligarRoboConsulta();
        useToast().success("Robos ligados com sucesso");
        executeBuscaStatusConsulta();
      } catch {
        useToast().error("Falha ao ligar os Robos");
      }
    };

    const executeDesligarRoboCadastro = async () => {
      try {
        await useDashboardService().desligarRoboCadastro();
        useToast().success("Robos desligados com sucesso");
        executeBuscaStatusCadastro();
      } catch {
        useToast().error("Falha ao desligar os Robos");
      }
    };

    const executeLigarRoboCadastro = async () => {
      try {
        await useDashboardService().ligarRoboCadastro();
        useToast().success("Robos ligados com sucesso");
        executeBuscaStatusCadastro();
      } catch {
        useToast().error("Falha ao ligar os Robos");
      }
    };

    const executeSearchFilaRobo = () => {
      const { data, getDadosFilaRobo } = useDashboardService();
      state.loadingFila = true;
      return getDadosFilaRobo(state.page)
        .then(() => {
          if (data.value && data.value.content) {
            const resultTratado: DashboarPayload[] = data.value.content.map(
              (item: DashboarPayload, index: number) => {
                item.groupIndex = index;
                return item;
              }
            );
            data.value.content = resultTratado;
            state.results = data.value;
          } else {
            state.results = defaultResults();
          }
        })
        .finally(() => {
          state.loadingFila = false;
        });
    };

    const executeBuscaOrgaosRobo = () => {
      const { data, getOrgaosRoboStatus } = useDashboardService();
      state.loadingOrgaos = true;
      return getOrgaosRoboStatus()
        .then(() => {
          state.orgaosRobo = Array.isArray(data.value) ? data.value : [];
        })
        .finally(() => {
          state.loadingOrgaos = false;
        });
    };

    const orgaoStatusClass = (orgao: OrgaoRoboStatusPayload) => {
      const consulta = !!orgao.consultaHabilitado;
      const cadastro = !!orgao.cadastroHabilitado;
      if (consulta && cadastro) {
        return "orgao-status-on";
      }
      if (!consulta && !cadastro) {
        return "orgao-status-off";
      }
      return "orgao-status-mixed";
    };

    const toggleConsultaOrgao = (
      orgao: OrgaoRoboStatusPayload,
      valor: boolean
    ) => {
      if (!orgao.idOrgao) {
        return;
      }
      const service = useDashboardService();
      const acao = valor
        ? service.ligarRoboConsultaOrgao(orgao.idOrgao)
        : service.desligarRoboConsultaOrgao(orgao.idOrgao);
      acao
        .then(() => {
          useToast().success(
            `Robô de Consulta ${valor ? "ligado" : "desligado"} para ${
              orgao.sigla
            }`
          );
        })
        .finally(() => {
          executeBuscaOrgaosRobo();
        });
    };

    const toggleCadastroOrgao = (
      orgao: OrgaoRoboStatusPayload,
      valor: boolean
    ) => {
      if (!orgao.idOrgao) {
        return;
      }
      const service = useDashboardService();
      const acao = valor
        ? service.ligarRoboCadastroOrgao(orgao.idOrgao)
        : service.desligarRoboCadastroOrgao(orgao.idOrgao);
      acao
        .then(() => {
          useToast().success(
            `Robô de Cadastro ${valor ? "ligado" : "desligado"} para ${
              orgao.sigla
            }`
          );
        })
        .finally(() => {
          executeBuscaOrgaosRobo();
        });
    };

    const getIdOrgaoAetBySigla = (sigla?: string | null) => {
      if (!sigla) {
        return null;
      }
      return (
        useOrgaoAetService()
          .getOrgaosAet()
          .find((o) => o.sigla === sigla)?.idOrgao ?? null
      );
    };

    const getIdOrgaoArtBySigla = (sigla?: string | null) => {
      if (!sigla) {
        return null;
      }
      return (
        useOrgaoArtService()
          .getOrgaosArt()
          .find((o) => o.sigla === sigla)?.idOrgao ?? null
      );
    };

    const getFila = (e?: DashboarPayload) => {
      return useDashboardService().getFila(e);
    };

    const getSigla = (e?: DashboarPayload) => {
      return useDashboardService().getSigla(e);
    };

    const getStatusRobo = (e?: DashboarPayload) => {
      return useDashboardService().getStatusRobo(e);
    };

    const getQuantidade = (e?: DashboarPayload) => {
      return useDashboardService().getQuantidade(e);
    };

    onMounted(() => {
      executeBuscaStatusCadastro();
      executeBuscaStatusConsulta();
      executeSearchFilaRobo();
      executeBuscaOrgaosRobo();
      executeBuscaSaldoAntiCaptcha();
      executeBuscaSaldoHcaptcha();
    });

    return {
      state,
      amIAllowed,
      executeBuscaStatusCadastro,
      executeBuscaStatusConsulta,
      executeDesligarRoboConsulta,
      executeLigarRoboConsulta,
      executeDesligarRoboCadastro,
      executeLigarRoboCadastro,
      executeBuscaSaldoHcaptcha,
      executeBuscaSaldoAntiCaptcha,
      executeSearchFilaRobo,
      executeBuscaOrgaosRobo,
      orgaoStatusClass,
      getIdOrgaoAetBySigla,
      getIdOrgaoArtBySigla,
      toggleConsultaOrgao,
      toggleCadastroOrgao,
      getFila,
      getSigla,
      getStatusRobo,
      getQuantidade
    };
  }
});
